import React from "react";
import PropTypes from "prop-types";
import { PromiseState } from "react-refetch";
import saveAs from "file-saver";
import { injectIntl, intlShape, FormattedMessage } from "react-intl";
import jsPDF from "jspdf";

import AlmigButton from "../grundelemente/AlmigButton";
import AlmigDropDown, {genAlmigDropdownItems, genAlmigDropdownTitle } from "../grundelemente/AlmigDropDown";
import Zeitraum from "../grundelemente/Zeitraum";

import { cachingRefetchWithToken } from "../netzwerk/Refetch";
import { postJSONexpectJSON } from "../netzwerk/FetchWithToken";

import { getUnit } from "../einheiten/Units";

import AlmigLogo from "./ALMIG_Logo.png";

import moment from "moment";
import _ from "lodash";

import "./EnergieBilanz.css";

import {
	CHARTS_DATA_SOURCE_LIVE_STANDARD,
	CHARTS_DATA_SOURCE_SD_CARD_LOG_FILES,
	chartsDataSourceIndex
} from "../model/Diagramme"
import { PURPOSE_ENERGY_BALANCE_CSV, PURPOSE_ENERGY_BALANCE_PDF } from "../model/HeDataLogs";

const rangeToFileName = (range, extension) => {
	let from = moment(range.from_ms);
	let until = moment(range.until_ms);

	return "balance_" + from.format("YYYY-MMM-DD") + "-" + until.format("YYYY-MMM-DD") + "." + extension;
};

const prepareBalanceUnits = (units, intl) => {
	return {
		currency_unit: getUnit(units["currency"]).genUnitText(intl),
		pressure_unit: getUnit(units["pressure"]).genUnitText(intl),
		percent_unit: getUnit(units["percent"]).genUnitText(intl),
		volume_unit: getUnit(units["volume"]).genUnitText(intl),
		energy_unit: getUnit(units["energy"]).genUnitText(intl),
		energy_over_volume_over_minutes_unit: getUnit(units["energy_over_volume_over_minutes"]).genUnitText(intl),
		volume_over_minutes_unit: getUnit(units["volume_over_minutes"]).genUnitText(intl),
		currency_over_volume_unit: getUnit(units["currency_over_volume"]).genUnitText(intl)
	};
};

/*
const DUMMY_BUNITS = {
	currency_unit: "€",
	pressure_unit: "bar",
	percent_unit: "%",
	volume_unit: "m³",
	energy_unit: "kW",
	energy_over_volume_over_minutes_unit: "kW/(m³/min)",
	volume_over_minutes_unit: "m³/min",
	currency_over_volume_unit: "€/m³"
};
*/

const prepareBalanceData = (data, compressors_ablauf, user_units) => {
	/*
	 * bisschen konstante werte vorbereiten
	 */
	let from = moment(data.range.from_ms).format("DD.MM.YYYY");
	let until = moment(data.range.until_ms).format("DD.MM.YYYY");

	let energy_cost = user_units.electricity_rate;

	/*
	 * daten aufbereiten 1: kompressoren
	 */
	const empty_compressor = {
		cindex: 0,
		anzahl_entlastungen: 0,
		anzahl_motor_starts: 0,
		energie_lastlauf_1kWh0: 0,
		energie_leerlauf_1kWh0: 0,
		leistung_max_1kW0: 0,
		leistung_min_1kW0: 0,
		liefermenge_max_1m3h0: 0,
		liefermenge_min_1m3h0: 0,
		volumen_liefermenge_1m30: 0,
		zeit_lastlauf_1s0: 0,
		zeit_leerlauf_1s0: 0
	};
	const compressors = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map(n => {
		const i = _.findIndex(data.compressors, c => c.cindex === n);
		let c = null;
		if (i !== -1) {
			c = _.clone(data.compressors[i]);
		}
		else {
			c = _.clone(empty_compressor);
		}

		/*
		 * clamp-werte fixen
		 */
		if (c.liefermenge_max_1m3h0 < 0)
			c.liefermenge_max_1m3h0 = 0;
		if (c.leistung_max_1kW0 < 0)
			c.leistung_max_1kW0 = 0;
		if (c.liefermenge_min_1m3h0 > 1e29)	// in den c-sourcen ist das 1e30, sollte also safe sein!
			c.liefermenge_min_1m3h0 = 0;
		if (c.leistung_min_1kW0 > 1e29)
			c.leistung_min_1kW0 = 0;

		/*
		 * ok, jetzt haben wir gültige rohdaten. nun aufpimpen!
		 */
		c.cindex = n + 1;
		c.energie_gesamt_1kWh0 = c.energie_lastlauf_1kWh0 + c.energie_leerlauf_1kWh0;
		c.zeit_gesamt_1s0 = c.zeit_lastlauf_1s0 + c.zeit_leerlauf_1s0;
		c.kosten_lastlauf = c.energie_lastlauf_1kWh0 * energy_cost;
		c.kosten_leerlauf = c.energie_leerlauf_1kWh0 * energy_cost;
		c.kosten_gesamt = c.kosten_lastlauf + c.kosten_leerlauf;

		c.name = "";
		if (compressors_ablauf.length > n && compressors_ablauf[n] !== null) {
			c.name = compressors_ablauf[n].name;
		}

		/*
		 * werte mit bisschen mehr gerechne
		 */
		c.auslastung = (c.zeit_gesamt_1s0 > 0) ? ((c.zeit_lastlauf_1s0 * 100) / c.zeit_gesamt_1s0) : 0;
		c.spezifische_leistung = (c.volumen_liefermenge_1m30 > 0) ? (c.energie_gesamt_1kWh0 / c.volumen_liefermenge_1m30) : 0;
		c.leistung_leerlauf = (c.zeit_leerlauf_1s0 > 0) ? (c.energie_leerlauf_1kWh0 / (c.zeit_leerlauf_1s0 / 3600)) : 0;

		return c;
	});

	/*
	 * daten aufbereiten 2: station
	 * der index ist wegen dem sqlite-mapping im backend(!)
	 */
	const plant =
	{
		netzdruck_min_1bar0: data.plant[0].netzdruck_min_1bar0 || 0,
		netzdruck_max_1bar0: data.plant[0].netzdruck_max_1bar0 || 0
	};
	const sums = compressors.reduce((accu, curr) => {
		accu.anzahl_motor_starts += curr.anzahl_motor_starts;
		accu.anzahl_entlastungen += curr.anzahl_entlastungen;
		accu.volumen_liefermenge_1m30 += curr.volumen_liefermenge_1m30;
		accu.energie_lastlauf_1kWh0 += curr.energie_lastlauf_1kWh0;
		accu.energie_leerlauf_1kWh0 += curr.energie_leerlauf_1kWh0;
		accu.energie_gesamt_1kWh0 += curr.energie_gesamt_1kWh0;
		accu.gesamt_kosten_lastlauf += curr.kosten_lastlauf;
		accu.gesamt_kosten_leerlauf += curr.kosten_leerlauf;

		return accu;
	}, {
		anzahl_motor_starts: 0,
		anzahl_entlastungen: 0,
		volumen_liefermenge_1m30: 0,
		energie_lastlauf_1kWh0: 0,
		energie_leerlauf_1kWh0: 0,
		energie_gesamt_1kWh0: 0,
		gesamt_kosten_lastlauf: 0,
		gesamt_kosten_leerlauf: 0
	});
	sums.gesamt_kosten = sums.gesamt_kosten_lastlauf + sums.gesamt_kosten_leerlauf;
	console.log("bData generation sum accu is: " + JSON.stringify(sums));
	const gesamt_spezifische_leistung = (sums.volumen_liefermenge_1m30 > 0) ? (sums.energie_gesamt_1kWh0 / sums.volumen_liefermenge_1m30): 0;
	const druckluftkosten = (sums.volumen_liefermenge_1m30 > 0) ? (sums.gesamt_kosten / sums.volumen_liefermenge_1m30): 0;
	const leerlaufkosten = (sums.gesamt_kosten > 0) ? ((sums.gesamt_kosten_leerlauf * 100) / sums.gesamt_kosten): 0;
	const lastkosten = (sums.gesamt_kosten > 0) ? ((sums.gesamt_kosten_lastlauf * 100) / sums.gesamt_kosten): 0;

	return {
		from,
		until,
		compressors,
		plant,
		sums,
		gesamt_spezifische_leistung,
		druckluftkosten,
		leerlaufkosten,
		lastkosten,
		energy_cost
	};
};

/*
const DUMMY_BDATA = {
	from: moment().subtract(1, "days").format("DD.MM.YYYY"),
	until: moment().format("DD.MM.YYYY"),
	compressors: (() => {
		return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => { return {
			cindex: num,
			name: "compressor " + num,
			liefermenge_min_1m3h0: (num * 10) + 2,
			liefermenge_max_1m3h0: (num * 10) + 3,
			leistung_min_1kW0: (num * 10) + 4,
			leistung_max_1kW0: (num * 10) + 5,
			leistung_leerlauf: (num * 10) + 6,
			zeit_lastlauf_1s0: (num * 10) + 7,
			zeit_leerlauf_1s0: (num * 10) + 8,
			auslastung: (num * 10) + 9,
			anzahl_motor_starts: (num * 10) + 10,
			anzahl_entlastungen: (num * 10) + 11,
			energie_lastlauf_1kWh0: (num * 10) + 12,
			energie_leerlauf_1kWh0: (num * 10) + 13,
			energie_gesamt_1kWh0: (num * 10) + 14,
			spezifische_leistung: (num * 10) + 15,
			kosten_lastlauf: (num * 10) + 16,
			kosten_leerlauf: (num * 10) + 17,
			kosten_gesamt: (num * 10) + 18,
			volumen_liefermenge_1m30: (num * 10) + 19,
			zeit_gesamt_1s0: (num * 10) + 20};
		});
	})(),
	plant: {
		netzdruck_min_1bar0: 1.1,
		netzdruck_max_1bar0: 2.2
	},
	sums: {
		anzahl_motor_starts: 11,
		anzahl_entlastungen: 12,
		volumen_liefermenge_1m30: 13,
		energie_lastlauf_1kWh0: 14,
		energie_leerlauf_1kWh0: 15,
		energie_gesamt_1kWh0: 16,
		gesamt_kosten_lastlauf: 17,
		gesamt_kosten_leerlauf: 18,
		gesamt_kosten: 19
	},
	gesamt_spezifische_leistung: 3.3,
	druckluftkosten: 4.4,
	leerlaufkosten: 5.5,
	lastkosten: 6.6
};
*/

const balanceDataToReportCSV = (bData, bUnits, intl) => {
	/*
	 * backe, backe, bilanz!
	 */
	const tr = (id) => intl.formatMessage({id});
	const fn = (value, digits) => {
		return intl.formatNumber(
			!isNaN(value) ? value : 0,
			{
				style: "decimal",
				minimunIntegerDigits: 1,
				minimumFractionDigits: digits,
				maximumFractionDigits: digits
			}
		);
	};
	const lf = "\r\n";
	const tb = ";";
	let result = lf;	// wir fangen in der zweiten zeile an. warum? kP.

	/*
	 * kopfzeile
	 */
	result += tb + tr("Energy and compressed air balance") + tb + bData.from + tb + bData.until + lf + lf;
	result += tb + tr("Total specific power") + tb + fn(bData.gesamt_spezifische_leistung * 60, 5) + tb + bUnits.energy_over_volume_over_minutes_unit + lf;	// hier mit faktor, weil: 60 (kW/(m³/min)) == 1 (kWh/m³). und letzteres ist unseres ausgangsgrösse
	result += tb + tr("Total specific power") + tb + fn(bData.gesamt_spezifische_leistung, 5) + tb + bUnits.energy_unit + "/" + bUnits.volume_unit + lf;
	result += tb + tr("Cost compressed air") + tb + fn(bData.druckluftkosten, 5) + tb + bUnits.currency_over_volume_unit + lf;
	result += tb + tr("Electricity rate") + tb + fn(bData.energy_cost, 3) + tb + bUnits.currency_unit + "/" + bUnits.energy_unit + lf;
	result += tb + tr("P-min") + tb + fn(bData.plant.netzdruck_min_1bar0, 1) + tb + bUnits.pressure_unit + lf;
	result += tb + tr("P-max") + tb + fn(bData.plant.netzdruck_max_1bar0, 1) + tb + bUnits.pressure_unit + lf;
	result += tb + tr("Cost load") + tb + fn(bData.lastkosten, 2) + tb + bUnits.percent_unit + lf;
	result += tb + tr("Cost non-load") + tb + fn(bData.leerlaufkosten, 2) + tb + bUnits.percent_unit + lf;
	result += tb + tr("Total cost") + tb + fn(bData.sums.gesamt_kosten, 2) + tb + bUnits.currency_unit + lf + lf;

	/*
	 * Kompressoren
	 */

	result += tr("Channel") + tb + tr("Compressor") + tb + bUnits.volume_over_minutes_unit + tb + tb + tr("Load") + " " + tr("kW") + tb + tb + tr("kW") + tb + tr("Load") + tb + tb + tr("No-load") + tb + tb + tr("Load factor") + tb + tr("Switches") + tb + tb + tr("Compressed air") + tb + tr("Total") + " " + bUnits.energy_unit + tb + tb + tb + tr("Specific power") + tb + tr("Total cost") + " " + bUnits.currency_unit + lf;
	result += tb + tb + tr("min") + tb + tr("max") + tb + tr("min") + tb + tr("max") + tb + tr("No-load") + tb + tr("h") + tb + tr("min") + tb + tr("h") + tb + tr("min") + tb + tr("Load") + tb + tr("Motor") + tb + tr("Load") + tb + bUnits.volume_unit + tb + tr("Load") + tb + tr("No-load") + tb + tr("Total") + tb + bUnits.energy_unit + "/" + bUnits.volume_unit + tb + tr("Load") + tb + tr("No-load") + tb + tr("Total") + lf;

	bData.compressors.forEach(c => {
		result += c.cindex
			+ tb + c.name
			+ tb + fn(c.liefermenge_min_1m3h0 / 60, 1)						// wir wollen pro minute
			+ tb + fn(c.liefermenge_max_1m3h0 / 60, 1)
			+ tb + fn(c.leistung_min_1kW0, 2)
			+ tb + fn(c.leistung_max_1kW0, 2)
			+ tb + fn(c.leistung_leerlauf, 2)
			+ tb + fn(Math.trunc(c.zeit_lastlauf_1s0 / 3600), 0)			// ohne .trunc kommen hier falsche Zeiten raus, wegen Rundung
			+ tb + fn(Math.trunc((c.zeit_lastlauf_1s0 % 3600) / 60), 0)
			+ tb + fn(Math.trunc(c.zeit_leerlauf_1s0 / 3600), 0)
			+ tb + fn(Math.trunc((c.zeit_leerlauf_1s0 % 3600) / 60), 0)
			+ tb + fn(c.auslastung, 1)
			+ tb + fn(c.anzahl_motor_starts, 0)
			+ tb + fn(c.anzahl_entlastungen, 0)
			+ tb + fn(c.volumen_liefermenge_1m30, 0)
			+ tb + fn(c.energie_lastlauf_1kWh0, 0)
			+ tb + fn(c.energie_leerlauf_1kWh0, 0)
			+ tb + fn(c.energie_gesamt_1kWh0, 0)
			+ tb + fn(c.spezifische_leistung, 5)
			+ tb + fn(c.kosten_lastlauf, 2)
			+ tb + fn(c.kosten_leerlauf, 2)
			+ tb + fn(c.kosten_gesamt, 2)
			+ lf;
	});

	/*
	 * Summen
	 */
	result += tb + tb + tb + tb + tb + tb + tb + tb + tb + tb + tb + tr("Sum")
		+ tb + fn(bData.sums.anzahl_motor_starts, 0)
		+ tb + fn(bData.sums.anzahl_entlastungen, 0)
		+ tb + fn(bData.sums.volumen_liefermenge_1m30, 0)
		+ tb + fn(bData.sums.energie_lastlauf_1kWh0, 0)
		+ tb + fn(bData.sums.energie_leerlauf_1kWh0, 0)
		+ tb + fn(bData.sums.energie_gesamt_1kWh0, 0)
		+ tb + fn(bData.gesamt_spezifische_leistung, 5)
		+ tb + fn(bData.sums.gesamt_kosten_lastlauf, 2)
		+ tb + fn(bData.sums.gesamt_kosten_leerlauf, 2)
		+ tb + fn(bData.sums.gesamt_kosten, 2)
		+ lf;

	return result;
};

const balanceDataToReportPDF = (bData, bUnits, settings, filename, intl, version) => {
	const X_OFFSETS = [0, 8, 40, 52, 64, 76, 88, 100, 111, 122, 133, 144, 156, 168, 180, 192, 204, 216, 228, 243, 255, 267, 279];	// -> A4, 9mm rand
	const Y_OFFSETS = [0, 6, 10, 14, 18, 22, 26, 30, 34, 38, 42, 48, 52, 56, 60, 64, 68, 72, 76, 80, 84, 88, 92, 96, 100, 106];
	const DRAW_COLOR = "#000000";
	const TEXT_COLOR = "#000000";
	const FILL_COLOR_BLUE = "#0077bb";
	const FILL_COLOR_YELLOW = "#f7a600";
	const TEXT_SPACING = 0.75;

	const tr = (id) => intl.formatMessage({id});
	const fn = (value, digits) => {
		return intl.formatNumber(
			!isNaN(value) ? value : 0,
			{
				style: "decimal",
				minimunIntegerDigits: 1,
				minimumFractionDigits: digits,
				maximumFractionDigits: digits
			}
		);
	};

	const generateContext = (pdf, baseX = 0, baseY = 0, scaleX = 1, scaleY = 1) => {
		let curr_x = 0;
		let curr_y = 0;
		let draw_color = DRAW_COLOR;
		let text_color = TEXT_COLOR;
		let fill_color = FILL_COLOR_BLUE;
		let line_width = 0.5;
		let font_size = 12;
		let font_style = "normal";

		const getCoord = (span_w = 1, span_h = 1) => {
			const x_i = X_OFFSETS[curr_x] * scaleX;
			const y_i = Y_OFFSETS[curr_y] * scaleY;

			return {
				x: x_i + baseX,
				y: y_i + baseY,
				w: X_OFFSETS[curr_x + span_w] * scaleX - x_i,
				h: Y_OFFSETS[curr_y + span_h] * scaleY - y_i
			};
		};

		const goto = (x, y) => {
			curr_x = x;
			curr_y = y;
		};
		const move = (x, y) => {
			curr_x += x;
			curr_y += y;
		};
		const setTextColor = (color) => {
			text_color = color;
		};
		const setDrawColor = (color) => {
			draw_color = color;
		};
		const setFillColor = (color) => {
			fill_color = color;
		};
		const setLineWidth = (width) => {
			line_width = width;
		};
		const setFontSize = (size) => {
			font_size = size;
		};
		const setFontStyle = (style) => {
			font_style = style;
		};

		const drawRect = (span_w = 1, span_h = 1) => {
			const { x, y, w, h } = getCoord(span_w, span_h);

			pdf.setDrawColor(draw_color);
			pdf.setLineWidth(line_width);
			pdf.rect(x, y, w, h, "S");

			move(span_w, span_h);
		};
		const fillRect = (span_w = 1, span_h = 1) => {
			const { x, y, w, h } = getCoord(span_w, span_h);

			pdf.setFillColor(fill_color);
			pdf.rect(x, y, w, h, "F");

			move(span_w, span_h);
		};
		const textCellLeft = (text, span_w = 1, span_h = 1) => {
			const { x, y, w, h } = getCoord(span_w, span_h);

			pdf.setTextColor(text_color);
			pdf.setFontSize(font_size);
			pdf.setFontStyle(font_style);
			pdf.setLineWidth(line_width);
			pdf.text(text, x + TEXT_SPACING, y + h / 2, {
				align: "left",
				baseline: "middle",
				maxWidth: w - (2 * TEXT_SPACING)
			});
		};
		const textCellCenter = (text, span_w = 1, span_h = 1) => {
			const { x, y, w, h } = getCoord(span_w, span_h);

			pdf.setTextColor(text_color);
			pdf.setFontSize(font_size);
			pdf.setFontStyle(font_style);
			pdf.setLineWidth(line_width);
			pdf.text(text, x + w / 2, y + h / 2, {
				align: "center",
				baseline: "middle",
				maxWidth: w - (2 * TEXT_SPACING)
			});
		};
		const textCellRight = (text, span_w = 1, span_h = 1) => {
			const { x, y, w, h } = getCoord(span_w, span_h);

			pdf.setTextColor(text_color);
			pdf.setFontSize(font_size);
			pdf.setFontStyle(font_style);
			pdf.setLineWidth(line_width);
			pdf.text(text, x + w - TEXT_SPACING, y + h / 2, {
				align: "right",
				baseline: "middle",
				maxWidth: w - (2 * TEXT_SPACING)
			});
		};
		const logo = (img, size_w = 1, size_h = 1) => {
			const x_i = X_OFFSETS[X_OFFSETS.length - 1] + baseX;
			pdf.addImage(
				img,
				'PNG',
				x_i - size_w,
				baseY,
				size_w,
				size_h);
		}

		return {
			goto,
			move,
			setDrawColor,
			setFillColor,
			setTextColor,
			setFontSize,
			setFontStyle,
			setLineWidth,
			drawRect,
			fillRect,
			textCellLeft,
			textCellCenter,
			textCellRight,
			logo
		};
	};

	/*
	 * settings sicherheitshalber aufbereiten!
	 */
	let size = settings.print.paper_size.toLowerCase();
	//let orientation = settings.print.orientation.toLowerCase(); wir machen hier immer landscape!

	let pdf;
	try {
		pdf = new jsPDF(
			"landscape",
			"mm",
			size);
	}
	catch (e) {
		/*
			* wenn irgendwas trotzdem nicht klappt, dann interne defaults
			* con jspdf verwenden.
			*/
		pdf = new jsPDF();
	}
	let ctx = null;
	switch(size) {
	default:	// sicherheitshalber
	case "a4":
		ctx = generateContext(pdf, 9, 52, 1, 1);
		break;
	case "letter":
		ctx = generateContext(pdf, 9, 55, (261 / 279), 1);
		break;
	}

	ctx.setTextColor("#111111");
	ctx.setDrawColor("#111111");

	/*
	 * als allererstes: hintergruende faerben!
	 */

	// kopf der summentabelle: Faerben
	ctx.setFillColor(FILL_COLOR_BLUE);
	ctx.goto(0, 0);
	ctx.fillRect(9, 1);

	// kopf der kompressortabelle: Faerben
	ctx.goto(0, 11);
	ctx.fillRect(22, 3);

	// kompressortabelle: summenfelder faerben
	ctx.setFillColor(FILL_COLOR_YELLOW);
	ctx.goto(11, 24);
	ctx.fillRect(11, 1);

	/*
	 * erst duenne linien zeichnen, die dicken verdecken dann besser
	 */

	// summentabelle: zeilen
	ctx.setLineWidth(0.08);
	for (let i = 0; i < 9; ++i) {
		ctx.goto(0, i + 1);
		ctx.drawRect(9, 1);
	}
	// kompressortabelle: zeilen
	for (let i = 0; i < 10; ++i) {
		ctx.goto(0, i + 14);
		ctx.drawRect(22, 1);
	}
	// kompressortabelle: spalten
	for (let i = 0; i < 20; ++i) {
		ctx.goto(2 + i, 14);
		ctx.drawRect(1, 10);
	}
	// kompressortabelle: restliche kopfdeko
	for (let i = 0; i < 9; ++i) {
		ctx.goto(i + 2, 13);
		ctx.drawRect(1, 1);
	}
	ctx.goto(14, 13);
	ctx.drawRect(1, 1);
	ctx.goto(18, 13);
	ctx.drawRect(1, 1);
	ctx.goto(15, 12);
	ctx.drawRect(1, 2);
	ctx.goto(16, 12);
	ctx.drawRect(1, 2);
	ctx.goto(17, 12);
	ctx.drawRect(1, 2);
	ctx.goto(19, 12);
	ctx.drawRect(1, 2);
	ctx.goto(20, 12);
	ctx.drawRect(1, 2);
	ctx.goto(21, 12);
	ctx.drawRect(1, 2);

	/*
	 * dann dicke linien
	 */

	ctx.setLineWidth(0.40);
	// kopf der summentabelle: Rahmen
	ctx.goto(0, 0);
	ctx.drawRect(9, 1);
	// summentabelle: spalten
	ctx.goto(0, 1);
	ctx.drawRect(9, 9);
	ctx.goto(5, 0);
	ctx.drawRect(2, 10);
	ctx.goto(7, 0);
	ctx.drawRect(2, 10);

	// kopf der kompressortabelle: Rahmen
	ctx.goto(0, 11);
	ctx.drawRect(22, 3);
	// kompressortabelle: dicke spalten
	[1, 1, 2, 2, 1, 2, 2, 1, 1, 1, 1, 3, 1, 3].reduce((accu, curr) => {

		ctx.goto(accu, 11);
		ctx.drawRect(curr, 13);
		return accu + curr;
	}, 0);
	// kompressortabelle: summenfelder
	for (let i = 0; i < 11; ++i) {
		ctx.goto(i + 11, 24);
		ctx.drawRect(1, 1);
	}
	// schriftart fuer info-inhalte
	ctx.setFontSize(9);
	ctx.goto(11, 6);
	ctx.textCellLeft(tr("Webserver Version") + " " + version, 10, 1);
	ctx.goto(11, 7);
	ctx.textCellLeft(tr("Report generated on") + " " + moment().format("DD.MM.YYYY"), 10, 1);

	// schriftart fuer alle inhalte
	ctx.setFontSize(7);

	// in der summentabelle ist alles fett
	ctx.setFontStyle("bold");

	// einheiten summentabelle
	ctx.goto(7, 1);
	ctx.textCellLeft(bUnits.energy_over_volume_over_minutes_unit, 2, 1);
	ctx.goto(7, 2);
	ctx.textCellLeft(bUnits.energy_unit + "/" + bUnits.volume_unit, 2, 1);
	ctx.goto(7, 3);
	ctx.textCellLeft(bUnits.currency_over_volume_unit, 2, 1);
	ctx.goto(7, 4);
	ctx.textCellLeft(bUnits.currency_unit + "/" + bUnits.energy_unit, 2, 1);
	ctx.goto(7, 5);
	ctx.textCellLeft(bUnits.pressure_unit, 2, 1);
	ctx.goto(7, 6);
	ctx.textCellLeft(bUnits.pressure_unit, 2, 1);
	ctx.goto(7, 7);
	ctx.textCellLeft(bUnits.percent_unit, 2, 1);
	ctx.goto(7, 8);
	ctx.textCellLeft(bUnits.percent_unit, 2, 1);
	ctx.goto(7, 9);
	ctx.textCellLeft(bUnits.currency_unit, 2, 1);

	// labels summentabelle
	ctx.goto(0, 1);
	ctx.textCellLeft(tr("Total specific power"), 5, 1);
	ctx.goto(0, 2);
	ctx.textCellLeft(tr("Total specific power"), 5, 1);
	ctx.goto(0, 3);
	ctx.textCellLeft(tr("Cost compressed air"), 5, 1);
	ctx.goto(0, 4);
	ctx.textCellLeft(tr("Electricity rate"), 5, 1);
	ctx.goto(0, 5);
	ctx.textCellLeft(tr("P-min"), 5, 1);
	ctx.goto(0, 6);
	ctx.textCellLeft(tr("P-max"), 5, 1);
	ctx.goto(0, 7);
	ctx.textCellLeft(tr("Cost load"), 5, 1);
	ctx.goto(0, 8);
	ctx.textCellLeft(tr("Cost non-load"), 5, 1);
	ctx.goto(0, 9);
	ctx.textCellLeft(tr("Total cost"), 5, 1);

	// inhalte summentabelle
	ctx.goto(5, 1);
	ctx.textCellRight(fn(bData.gesamt_spezifische_leistung * 60, 5), 2, 1);
	ctx.goto(5, 2);
	ctx.textCellRight(fn(bData.gesamt_spezifische_leistung, 5), 2, 1);
	ctx.goto(5, 3);
	ctx.textCellRight(fn(bData.druckluftkosten, 5), 2, 1);
	ctx.goto(5, 4);
	ctx.textCellRight(fn(bData.energy_cost, 3), 2, 1);
	ctx.goto(5, 5);
	ctx.textCellRight(fn(bData.plant.netzdruck_min_1bar0, 1), 2, 1);
	ctx.goto(5, 6);
	ctx.textCellRight(fn(bData.plant.netzdruck_max_1bar0, 1), 2, 1);
	ctx.goto(5, 7);
	ctx.textCellRight(fn(bData.lastkosten, 2), 2, 1);
	ctx.goto(5, 8);
	ctx.textCellRight(fn(bData.leerlaufkosten, 2), 2, 1);
	ctx.goto(5, 9);
	ctx.textCellRight(fn(bData.sums.gesamt_kosten, 2), 2, 1);

	ctx.setFontStyle("normal");

	// inhalte kompressortablle
	bData.compressors.forEach(c => {
		let i = c.cindex;
		ctx.goto(0, i + 13);
		ctx.textCellRight(fn(c.cindex, 0));
		ctx.goto(1, i + 13);
		ctx.setFontStyle("bold");
		ctx.textCellLeft(c.name);
		ctx.setFontStyle("normal");
		ctx.goto(2, i + 13);
		ctx.textCellRight(fn(c.liefermenge_min_1m3h0 / 60, 1));
		ctx.goto(3, i + 13);
		ctx.textCellRight(fn(c.liefermenge_max_1m3h0 / 60, 1));
		ctx.goto(4, i + 13);
		ctx.textCellRight(fn(c.leistung_min_1kW0, 2));
		ctx.goto(5, i + 13);
		ctx.textCellRight(fn(c.leistung_max_1kW0, 2));
		ctx.goto(6, i + 13);
		ctx.textCellRight(fn(c.leistung_leerlauf, 2));
		ctx.goto(7, i + 13);
		ctx.textCellRight(fn(Math.trunc(c.zeit_lastlauf_1s0 / 3600), 0));
		ctx.goto(8, i + 13);
		ctx.textCellRight(fn(Math.trunc((c.zeit_lastlauf_1s0 % 3600) / 60), 0));
		ctx.goto(9, i + 13);
		ctx.textCellRight(fn(Math.trunc(c.zeit_leerlauf_1s0 / 3600), 0));
		ctx.goto(10, i + 13);
		ctx.textCellRight(fn(Math.trunc((c.zeit_leerlauf_1s0 % 3600) / 60), 0));
		ctx.goto(11, i + 13);
		ctx.textCellRight(fn(c.auslastung, 1));
		ctx.goto(12, i + 13);
		ctx.textCellRight(fn(c.anzahl_motor_starts, 0));
		ctx.goto(13, i + 13);
		ctx.textCellRight(fn(c.anzahl_entlastungen, 0));
		ctx.goto(14, i + 13);
		ctx.setFontStyle("bold");
		ctx.textCellRight(fn(c.volumen_liefermenge_1m30, 0));
		ctx.setFontStyle("normal");
		ctx.goto(15, i + 13);
		ctx.textCellRight(fn(c.energie_lastlauf_1kWh0, 0));
		ctx.goto(16, i + 13);
		ctx.textCellRight(fn(c.energie_leerlauf_1kWh0, 0));
		ctx.goto(17, i + 13);
		ctx.setFontStyle("bold");
		ctx.textCellRight(fn(c.energie_gesamt_1kWh0, 0));
		ctx.setFontStyle("normal");
		ctx.goto(18, i + 13);
		ctx.textCellRight(fn(c.spezifische_leistung * 60, 5));	// hier mit faktor, weil: 60 (kW/(m³/min)) == 1 (kWh/m³). und letzteres ist unseres ausgangsgrösse
		ctx.goto(19, i + 13);
		ctx.textCellRight(fn(c.kosten_lastlauf, 2));
		ctx.goto(20, i + 13);
		ctx.textCellRight(fn(c.kosten_leerlauf, 2));
		ctx.goto(21, i + 13);
		ctx.setFontStyle("bold");
		ctx.textCellRight(fn(c.kosten_gesamt, 2));
		ctx.setFontStyle("normal");
	});

	// Summen der Kompressortabelle
	ctx.setFontStyle("bold");
	ctx.goto(11, 24);
	ctx.textCellLeft(tr("Sum"));
	ctx.goto(12, 24);
	ctx.textCellRight(fn(bData.sums.anzahl_motor_starts, 0));
	ctx.goto(13, 24);
	ctx.textCellRight(fn(bData.sums.anzahl_entlastungen, 0));
	ctx.goto(14, 24);
	ctx.textCellRight(fn(bData.sums.volumen_liefermenge_1m30, 0));
	ctx.goto(15, 24);
	ctx.textCellRight(fn(bData.sums.energie_lastlauf_1kWh0, 0));
	ctx.goto(16, 24);
	ctx.textCellRight(fn(bData.sums.energie_leerlauf_1kWh0, 0));
	ctx.goto(17, 24);
	ctx.textCellRight(fn(bData.sums.energie_gesamt_1kWh0, 0));
	ctx.goto(18, 24);
	ctx.textCellRight(fn(bData.gesamt_spezifische_leistung * 60, 5));	// hier mit faktor, weil: 60 (kW/(m³/min)) == 1 (kWh/m³). und letzteres ist unseres ausgangsgrösse
	ctx.goto(19, 24);
	ctx.textCellRight(fn(bData.sums.gesamt_kosten_lastlauf, 2));
	ctx.goto(20, 24);
	ctx.textCellRight(fn(bData.sums.gesamt_kosten_leerlauf, 2));
	ctx.goto(21, 24);
	ctx.textCellRight(fn(bData.sums.gesamt_kosten, 2));

	// Zu guter letzt noch die weissen Sachen.
	// labels spalten kompressoren:
	ctx.setFontStyle("bold");
	ctx.setTextColor("#FFFFFF");
	ctx.goto(0, 11);
	ctx.textCellCenter(tr("Compr unit"), 1, 1);	// span_h = 1 wegen umbruch
	ctx.goto(1, 11);
	ctx.textCellCenter(tr("Compressor"), 1, 3);
	ctx.goto(2, 11);
	ctx.textCellCenter(tr("FAD"), 2, 2);
	ctx.goto(4, 11);
	ctx.textCellCenter(tr("Load"), 2, 2);
	ctx.goto(6, 11);
	ctx.textCellCenter(tr("No-load"), 1, 2);
	ctx.goto(7, 11);
	ctx.textCellCenter(tr("Load"), 2, 2);
	ctx.goto(9, 11);
	ctx.textCellCenter(tr("No-load"), 2, 2);
	ctx.goto(11, 11);
	ctx.textCellCenter(tr("Load factor"), 1, 2);	// span_h = 2 wegen umbruch
	ctx.goto(12, 11);
	ctx.textCellCenter(tr("Motor starts"), 1, 2);	// span_h = 2 wegen umbruch
	ctx.goto(13, 11);
	ctx.textCellCenter(tr("Load changes"), 1, 2);	// span_h = 2 wegen umbruch
	ctx.goto(14, 11);
	ctx.textCellCenter(tr("Total FAD"), 1, 1);	// span_h = 1 wegen umbruch
	ctx.goto(15, 11);
	ctx.textCellCenter(tr("Energy") + " (" + bUnits.energy_unit + ")", 3, 1);
	ctx.goto(18, 11);
	ctx.setFontSize(5);
	ctx.textCellCenter(tr("Specific power"), 1, 1);	// span_h = 1 wegen umbruch
	ctx.setFontSize(7);
	ctx.goto(19, 11);
	ctx.textCellCenter(tr("Cost"), 3, 1);

	// labels spalten kompressoren restliche deko
	ctx.goto(2, 13);
	ctx.textCellCenter(tr("min"), 1, 1);
	ctx.goto(3, 13);
	ctx.textCellCenter(tr("max"), 1, 1);
	ctx.goto(4, 13);
	ctx.textCellCenter(tr("min"), 1, 1);
	ctx.goto(5, 13);
	ctx.textCellCenter(tr("max"), 1, 1);
	ctx.goto(6, 13);
	ctx.textCellCenter(bUnits.energy_unit, 1, 1);
	ctx.goto(7, 13);
	ctx.textCellCenter(tr("h"), 1, 1);
	ctx.goto(8, 13);
	ctx.textCellCenter(tr("min"), 1, 1);
	ctx.goto(9, 13);
	ctx.textCellCenter(tr("h"), 1, 1);
	ctx.goto(10, 13);
	ctx.textCellCenter(tr("min"), 1, 1);
	ctx.goto(14, 13);
	ctx.textCellCenter(bUnits.volume_unit, 1, 1);
	ctx.goto(15, 12);
	ctx.textCellCenter(tr("Load"), 1, 2);
	ctx.goto(16, 12);
	ctx.textCellCenter(tr("No-load"), 1, 2);
	ctx.goto(17, 12);
	ctx.textCellCenter(tr("Total"), 1, 2);
	ctx.goto(18, 13);
	ctx.textCellCenter(bUnits.energy_over_volume_over_minutes_unit, 1, 1);
	ctx.goto(19, 12);
	ctx.textCellCenter(tr("Load"), 1, 2);
	ctx.goto(20, 12);
	ctx.textCellCenter(tr("No-load"), 1, 2);
	ctx.goto(21, 12);
	ctx.textCellCenter(tr("Total"), 1, 2);

	// labels summenstabelle
	ctx.setFontSize(10);
	ctx.goto(0, 0);
	ctx.textCellLeft(tr("Energy and compressed air balance"), 5, 1);
	ctx.goto(5, 0);
	ctx.textCellCenter(bData.from, 2, 1);
	ctx.goto(7, 0);
	ctx.textCellCenter(bData.until, 2, 1);

	// logo
	ctx.logo(AlmigLogo, 168 * 0.21, 65 * 0.21);		// pixel des original png mal auge*pi-skalierung

	pdf.save(filename);
};

export const makeAndShowEnergyBalance = (data, compressors, settings, units, intl, type, version) => {
	const bData = prepareBalanceData(data, compressors, settings.units);
	const bUnits = prepareBalanceUnits(units, intl);
	switch(type) {
	case "csv":
		saveAs(
			new Blob(
				[balanceDataToReportCSV(bData, bUnits, intl)],
				{type: "text/comma-separated-values"}
			),
			rangeToFileName(data.range, "csv")
		);
		break;
	case "pdf":
		balanceDataToReportPDF(
			bData, bUnits, settings, rangeToFileName(data.range, "pdf"), intl, version);
		break;
	default:
		console.log("failed to understand which energy balance you actually want.");
		break;
	}
}

const handleDownload = (type, range, intl, units, compressors, settings, version) => {
	postJSONexpectJSON("/daily_statistics/get_energy_balance_data", range).then(response => {
		return response.text().then(raw => {
			if (response.ok) {
				const data = JSON.parse(raw);
				makeAndShowEnergyBalance(data, compressors, settings, units, intl, type, version)
			}
			else {
				console.log("get_energy_balance_data failed; " + JSON.stringify(response));
			}
		});
	});
};

/*
const handleDownload = (type, range, intl, units, compressors, settings, version) => {
	switch(type) {
	case "csv":
		saveAs(
			new Blob(
				[balanceDataToReportCSV(DUMMY_BDATA, DUMMY_BUNITS, intl)],
				{type: "text/comma-separated-values"}
			),
			"dummyreport.csv"
		);
		break;
	case "pdf":
		balanceDataToReportPDF(
			DUMMY_BDATA, DUMMY_BUNITS, settings, "dummyreport.pdf", intl, version);
		break;
	}
};
*/

const EnergieBilanzInt = injectIntl(props => {
	const startDownload = (type) => {
		if(props.dataSource === CHARTS_DATA_SOURCE_LIVE_STANDARD) {
			handleDownload(type, props.range, props.intl, props.units, props.compressors, props.settings, props.ablauf_version)
		}
		else { // props.dataSource === CHARTS_DATA_SOURCE_SD_CARD_LOG_FILES
			let purpose = PURPOSE_ENERGY_BALANCE_CSV
			if(type === "pdf") {
				purpose = PURPOSE_ENERGY_BALANCE_PDF
			}
			const requiredData = {
				type: type,
				range: props.range,
				intl: props.intl,
				units: props.units,
				compressors: props.compressors,
				settings: props.settings,
				version: props.ablauf_version
			}
			props.requestEnergyBalanceHeDataLogFiles(purpose, requiredData, props.sortedListOfHeDataLogFiles)
		}
	}
	return (
		<div className="EnergieBilanz">
			<Zeitraum
				range={props.range}
				min_date={props.min_date}
				max_date={props.max_date}
				onRangeChanged={props.handleRangeChange}
			/>
			<AlmigButton
				i18n_key="Download CSV"
				onClick={() => startDownload("csv") }
				disabled={props.range == null || props.range.until_ms == null || props.range.from_ms == null}/>
			<AlmigButton
				i18n_key="Download PDF"
				onClick={() => startDownload("pdf")}
				disabled={props.range == null || props.range.until_ms == null || props.range.from_ms == null}/>
		</div>
	);
});

EnergieBilanzInt.propTypes = {
	intl: intlShape.isRequired,
	min_date: PropTypes.instanceOf(Date),
	max_date: PropTypes.instanceOf(Date),
	units: PropTypes.object,
	settings: PropTypes.object,
	compressors: PropTypes.array,
	handleRangeChange: PropTypes.func.isRequired,
	ablauf_version: PropTypes.string,
	dataSource: PropTypes.number,
	sortedListOfHeDataLogFiles: PropTypes.array,
	requestEnergyBalanceHeDataLogFiles: PropTypes.func
};

class EnergieBilanz extends React.Component {

	static propTypes = {
		today: PropTypes.instanceOf(Date),
		availableFetch: PropTypes.instanceOf(PromiseState).isRequired,
		auth: PropTypes.object.isRequired,
		units: PropTypes.object,
		user_units: PropTypes.object,
		settings: PropTypes.object,
		compressors: PropTypes.array,
		ablauf_version: PropTypes.string,
		dataSource: PropTypes.number,
		sortedListOfHeDataLogFiles: PropTypes.array,
		setDataSource: PropTypes.func,
		requestEnergyBalanceHeDataLogFiles: PropTypes.func
	};

	constructor(props, context) {
		super(props, context);
		this.handleRangeChange = this.handleRangeChange.bind(this);
		this.state = {
			range: null
		};
	}

	getDateOfElementOfSortedListOfHeDataLogFiles(listElement) {
		let aDate = new Date();
		aDate.setFullYear(listElement.year);
		aDate.setMonth(listElement.month - 1);
		aDate.setDate(listElement.day);
		aDate.setHours(12);
		aDate.setMinutes(0);
		aDate.setSeconds(0);
		aDate.setMilliseconds(0);
		return aDate;
	}
	getMaxDateHeDataLogs() {
		if(
			(this.props.sortedListOfHeDataLogFiles == null) ||
			(this.props.sortedListOfHeDataLogFiles.length === 0)
		) {
			return undefined;
		}
		const lastIndex = this.props.sortedListOfHeDataLogFiles.length - 1;
		const lastElement = this.props.sortedListOfHeDataLogFiles[lastIndex];
		return this.getDateOfElementOfSortedListOfHeDataLogFiles(lastElement);
	}
	getMinDateHeDataLogs() {
		if(
			(this.props.sortedListOfHeDataLogFiles == null) ||
			(this.props.sortedListOfHeDataLogFiles.length === 0)
		) {
			return undefined;
		}
		const firstElement = this.props.sortedListOfHeDataLogFiles[0];
		return this.getDateOfElementOfSortedListOfHeDataLogFiles(firstElement);
	}
	getMaxDate() {
		if(this.props.dataSource === CHARTS_DATA_SOURCE_LIVE_STANDARD)		 {
			return this.props.today;
		}
		else { // CHARTS_DATA_SOURCE_SD_CARD_LOG_FILES
			return this.getMaxDateHeDataLogs();
		}
	}
	getMinDate() {
		if(this.props.dataSource === CHARTS_DATA_SOURCE_LIVE_STANDARD) {
			return new Date(0);
		}
		else { // CHARTS_DATA_SOURCE_SD_CARD_LOG_FILES
			return this.getMinDateHeDataLogs();
		}
	}
	handleRangeChange(range) {
		this.setState({ range });
	}
	componentDidUpdate(prevProps) {
		// https://reactjs.org/docs/react-component.html#componentdidupdate
		// You may call setState() immediately in componentDidUpdate()
		// but note that it must be wrapped in a condition like in the example above,
		// or you’ll cause an infinite loop
		if (this.props.sortedListOfHeDataLogFiles !== prevProps.sortedListOfHeDataLogFiles) {
			// Auswahl vorbesetzen auf einen Validen Wert
			if(this.props.dataSource === CHARTS_DATA_SOURCE_SD_CARD_LOG_FILES) {
				let maxDate = this.getMaxDate();
				if(maxDate === undefined) {
					maxDate = new Date(0);
				}
				const maxDateTime = maxDate.getTime();
				this.setState({
					range: {
						from_ms: maxDateTime,
						until_ms: maxDateTime,
					}
				})
			}
		}
	}
	render() {
		return (
			<div>
				<FormattedMessage id="Data Source"/>
				<AlmigDropDown
					id="chartsDataSourceIndex"
					title={genAlmigDropdownTitle(chartsDataSourceIndex, this.props.dataSource)}
				>
					{ genAlmigDropdownItems(chartsDataSourceIndex, this.props.setDataSource) }
				</AlmigDropDown>
				<EnergieBilanzInt
					min_date={this.getMinDate()}
					max_date={this.getMaxDate()}
					handleRangeChange={this.handleRangeChange.bind(this)}
					range={this.state.range}
					units={this.props.units}
					settings={this.props.settings}
					compressors={this.props.compressors}
					ablauf_version={this.props.ablauf_version}
					dataSource={this.props.dataSource}
					sortedListOfHeDataLogFiles={this.props.sortedListOfHeDataLogFiles}
					requestEnergyBalanceHeDataLogFiles={this.props.requestEnergyBalanceHeDataLogFiles}
				/>
			</div>
		);
	}
}

export default cachingRefetchWithToken(() => ({
	availableFetch: { url: "/daily_statistics/get_available_days", refreshInterval: 10000 }
}))(EnergieBilanz);
